import { UI } from "./classes/ui/ui";
import { FormUI } from "./classes/ui/form-ui";
import { ResultsUI } from "./classes/ui/results-ui";
import { SavedReportsUI } from "./classes/ui/saved-reports-ui";
import { FormData } from "./classes/form-data";
import { initializeDefaultDataArgs } from "./data/default-steps";

export class AdHocReportBuilder {
    private UI: UI;
    private formUI: FormUI;
    private resultsUI: ResultsUI;
    private savedReportsUI: SavedReportsUI;
    private formData = new FormData();

    constructor(wrapper: HTMLElement) {
        if (!wrapper) {
            console.log(
                "Given wrapper node not found. Make sure the HTML element exist. Please use the tool as new AdHocReportBuilder(document.getElementById('ad-hoc-report-builder'))"
            );
            return;
        }

        this.UI = new UI(wrapper);
        this.formUI = new FormUI(this, this.UI.col2);
        this.resultsUI = new ResultsUI(this, this.UI.col2);
        this.savedReportsUI = new SavedReportsUI(this, this.UI.col1);
    }

    async init(): Promise<void> {
        this.UI.setLoading(true);

        // Add steps
        await this.fillUpDataWithDefaults();

        await this.buildUi();

        this.UI.setLoading(false);
    }

    /**
     * Fill up Data class with default steps
     */
    async fillUpDataWithDefaults(): Promise<void> {
        const defaultDataArgs = await initializeDefaultDataArgs();

        for (const step of defaultDataArgs) {
            this.formData.addStep(step);
        }
    }

    /**
     * Build UI from data steps
     */
    async buildUi(): Promise<void> {
        // Build general UI
        this.UI.buildUi();

        // Build UI for form from steps
        this.formUI.buildUi(this.formData.getSteps());

        // Build UI for table
        this.resultsUI.buildUi();

        // Build UI for saved reports
        this.savedReportsUI.buildUi();

        // Get links for saved reports
        await this.savedReportsUI.loadExistingReports();
    }

    /**
     * Handle report submit and pagination
     * 
     * @param {number} pageNumber Pagination
     */
    async onReportSubmit(pageNumber: number = 1) {
        const reportName = this.formUI.getReportName();
        if (!reportName) {
            alert("Please enter a name for the report");
            return;
        }

        const steps = this.formData.getSteps();

        for (const step of steps) {
            if (!step.values.length) {
                alert(`${step.title} is missing a value.`);
                return;
            }
        }

        const report = {
            name: reportName,
            steps: {
                typeOfReport: steps[0].values,
                worksites: steps[1].values,
                dataType: steps[2].values,
                timeframe: steps[3].values,
                columns: steps[4].values,
                // visualization: steps[5].values
            },
            page: pageNumber
        }

        try {
            this.UI.setLoading(true);

            // @ts-ignore
            const fetchedData = await fetch(ergmapsScriptsObject.ajaxUrl, {
                method: "POST",
                credentials: 'same-origin',
                headers: new Headers({
                    'Content-Type': 'application/x-www-form-urlencoded'
                }),
                body: `action=get_data_for_ad_hoc_report&steps=${JSON.stringify(report.steps)}&page=${report.page}`
            });

            const fetchedJson: { success: boolean, message?: string, fields: string[], rows: string[][], count: number } = await fetchedData.json();

            if (!fetchedJson.success) {
                alert(fetchedJson.message);
                return;
            }

            this.resultsUI.displayData(fetchedJson.fields, fetchedJson.rows, fetchedJson.count, report.page);
        } catch (error) {
            console.log(error);
        } finally {
            this.UI.setLoading(false);
        }
    }

    /**
     * Handle CSV export
     */
    async onCsvExport() {
        const reportName = this.formUI.getReportName();
        if (!reportName) {
            alert("Please enter a name for the report");
            return;
        }

        const steps = this.formData.getSteps();

        for (const step of steps) {
            if (!step.values.length) {
                alert(`${step.title} is missing a value.`);
                return;
            }
        }

        const report = {
            name: reportName,
            steps: {
                typeOfReport: steps[0].values,
                worksites: steps[1].values,
                dataType: steps[2].values,
                timeframe: steps[3].values,
                columns: steps[4].values,
                // visualization: steps[5].values
            }
        }

        try {
            this.UI.setLoading(true);

            // @ts-ignore
            const fetchedData = await fetch(ergmapsScriptsObject.ajaxUrl, {
                method: "POST",
                credentials: 'same-origin',
                headers: new Headers({
                    'Content-Type': 'application/x-www-form-urlencoded'
                }),
                body: `action=export_ad_hoc_report_data_to_csv&steps=${JSON.stringify(report.steps)}&report_name=${report.name}`
            });

            const fetchedText = await fetchedData.text();

            if (fetchedText) {
                const downloadLink = document.createElement("a");
                const fileData = ['\ufeff' + fetchedText];

                const blobObject = new Blob(fileData, {
                    type: "text/csv;charset=utf-8;"
                });

                const url = URL.createObjectURL(blobObject);
                downloadLink.href = url;
                downloadLink.download = report.name + ".csv";

                document.body.appendChild(downloadLink);
                downloadLink.click();
                document.body.removeChild(downloadLink);
            } else {
                alert('Error while exporting the report.')
            }
        } catch (error) {
            console.log(error);
        } finally {
            this.UI.setLoading(false);
        }
    }

    /**
     * Handle Report save
     */
    async onReportSave() {
        const reportName = this.formUI.getReportName();
        if (!reportName) {
            alert("Please enter a name for the report");
            return;
        }

        const steps = this.formData.getSteps();

        for (const step of steps) {
            if (!step.values.length) {
                alert(`${step.title} is missing a value.`);
                return;
            }
        }

        const report = {
            name: reportName,
            steps: {
                typeOfReport: steps[0].values,
                worksites: steps[1].values,
                dataType: steps[2].values,
                timeframe: steps[3].values,
                columns: steps[4].values,
                // visualization: steps[5].values
            }
        }

        try {
            this.UI.setLoading(true);

            // @ts-ignore
            const fetchedData = await fetch(ergmapsScriptsObject.ajaxUrl, {
                method: "POST",
                credentials: 'same-origin',
                headers: new Headers({
                    'Content-Type': 'application/x-www-form-urlencoded'
                }),
                body: `action=save_data_for_ad_hoc_report&steps=${JSON.stringify(report.steps)}&report_name=${report.name}`
            });

            const fetchedJSON = await fetchedData.json();

            this.UI.setLoading(false);

            if (fetchedJSON.success) {
                // Add item to saved reports
                this.savedReportsUI.addReport(fetchedJSON.report_id, reportName);

                alert('Saved successfully.');
            } else {
                alert(fetchedJSON.message);
            }
        } catch (error) {
            this.UI.setLoading(false);
            alert(error);
        }
    }

    /**
     * Handle report deletion
     * 
     * @param {number} id
     */
    async onReportDeletion(id: number, target: HTMLElement) {
        this.UI.setLoading(true);

        // @ts-ignore
        fetch(ergmapsScriptsObject.ajaxUrl, {
            method: "POST",
            credentials: 'same-origin',
            headers: new Headers({
                'Content-Type': 'application/x-www-form-urlencoded'
            }),
            body: `action=delete_ad_hoc_report&report_id=${id}`
        })
            .then(res => res.json())
            .then(result => {
                if (result.success) {
                    target.parentElement.parentElement.remove();
                    alert('Report has been deleted.')
                } else {
                    alert('Error while deleting the report.')
                }
            })
            .finally(() => {
                this.UI.setLoading(false);
            });
    }

    /**
     * Load a report to the builder
     * 
     * @param {string} title
     * @param {Object} steps
     */
    loadReport(title: string, steps: {
        typeOfReport: (string | number | { title: string, name: string, compare?: string, value?: string })[]
        worksites: (string | number | { title: string, name: string, compare?: string, value?: string })[]
        dataType: (string | number | { title: string, name: string, compare?: string, value?: string })[]
        timeframe: (string | number | { title: string, name: string, compare?: string, value?: string })[]
        columns: (string | number | { title: string, name: string, compare?: string, value?: string })[]
    }) {
        this.formUI.setValuesOfSteps(title, steps);
    }
}

// @ts-ignore
window.AdHocReportBuilder = AdHocReportBuilder;
